import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type { GetBindingResponse } from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  renderJson,
  renderKeyValueBlock,
  resolveOutputMode,
} from "../../lib/output.js";

export class BindShowCommand extends M8tCommand {
  static paths = [["bind", "show"]];
  static usage = Command.Usage({
    description: "Show details for one binding.",
    details:
      "Looks up a binding by its slug across channel partitions and renders every field. The plaintext bot token is NEVER returned — only the Key Vault URI (botTokenSecretRef).",
    examples: [["Pretty key-value block", "$0 bind show cmo-tg"]],
  });

  bindingId = Option.String();
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });

    const data = await apiCall<GetBindingResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path: `/api/bindings/${encodeURIComponent(this.bindingId)}` },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return 0;
    }

    const { binding } = data;
    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "binding id", value: binding.bindingId },
        { key: "channel", value: binding.channel },
        { key: "worker", value: binding.agentName },
        { key: "bot username", value: binding.botUsername },
        { key: "status", value: binding.status },
        { key: "token (KV ref)", value: binding.botTokenSecretRef },
        { key: "created", value: binding.createdAt },
        { key: "created by", value: binding.createdBy },
      ]) + "\n",
    );
    return 0;
  }
}
